import React, { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';

const ChevronIcon = FiChevronDown as React.ElementType;

type FooterSection = {
  title: string;
  links: Array<{ label: string; href: string }>;
};

const FooterLinks: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const branches = [
    {
      name: 'Nex Computer Main',
      phones: [
        { label: '077 342 1388', href: 'tel:+94773421388' },
        { label: '075 800 2235', href: 'tel:+94758002235' },
      ],
    },
    {
      name: 'Nex Unity Plaza',
      phones: [{ label: '075 800 2263', href: 'tel:+94758002263' }],
    },
    {
      name: 'Nex Puththalam Junction',
      phones: [
        { label: '071 815 5475', href: 'tel:+94718155475' },
        { label: '075 800 2238', href: 'tel:+94758002238' },
      ],
    },
    {
      name: 'Nex Care',
      phones: [{ label: '076 381 5617', href: 'tel:+94763815617' }],
    },
    {
      name: 'Nex Entertainment',
      phones: [{ label: '078 372 2152', href: 'tel:+94783722152' }],
    },
  ];

  const footerSections: FooterSection[] = [
    {
      title: 'Shop',
      links: [
        { label: 'Laptops', href: '/laptop' },
        { label: 'Desktop PCs', href: '/desktop' },
        { label: 'Components', href: '/components' },
        { label: 'Accessories', href: '/accessories' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'Home', href: '/' },
        { label: 'Featured Products', href: '/#featured-products' },
        { label: 'About Us', href: '/about-us' },
        { label: 'Offers', href: '/offers' },
        { label: 'Contact Us', href: '/#contact' },
        { label: 'Request Quote', href: '/quotation' },
      ],
    },
    {
      title: 'Support',
      links: [{ label: 'Help Center', href: '/#contact' }],
    },
  ];

  const toggleSection = (sectionTitle: string) => {
    setOpenSection((current) => (current === sectionTitle ? null : sectionTitle));
  };

  return (
    <div className="grid w-full grid-cols-1 gap-4 text-left text-[clamp(0.8125rem,1.5vw,0.9375rem)] md:grid-cols-3 lg:grid-cols-[0.62fr_0.78fr_0.58fr_1.8fr] lg:gap-5">
      {footerSections.map((section) => {
        const isOpen = openSection === section.title;

        return (
          <div key={section.title} className="w-full border-b border-white/10 pb-3 md:border-b-0 md:pb-0">
            <button
              type="button"
              onClick={() => toggleSection(section.title)}
              className="flex w-full items-center justify-between text-left md:cursor-default"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-300/85">{section.title}</p>
              
              <motion.span
                className="text-indigo-200 md:hidden"
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <ChevronIcon size={16} />
              </motion.span>
            </button>

            <ul className="mt-2.5 hidden space-y-1.5 text-slate-300 md:block">
              {section.links.map((link) => (
                <li key={link.label}>
                  <a className="inline-flex rounded-md py-0.5 leading-5 transition-colors hover:text-indigo-300" href={link.href}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.ul
                  className="space-y-1.5 overflow-hidden pt-3 text-slate-300 md:hidden"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                >
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <a className="block rounded-md py-0.5 transition-colors hover:text-indigo-300" href={link.href}>
                        {link.label}
                      </a>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      <div className="w-full md:col-span-3 lg:col-span-1">
        <div className="mb-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-300/90">Branch Contacts</p>
        </div>

        <div className="grid overflow-hidden rounded-xl border border-white/10 bg-slate-950/35 text-slate-300 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          {branches.map((branch) => (
            <div
              key={branch.name}
              className="grid min-w-0 gap-1 border-b border-white/10 px-3 py-2.5 last:border-b-0 sm:border-r sm:[&:nth-child(even)]:border-r-0 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:border-r-0 lg:[&:nth-last-child(-n+2)]:border-b lg:last:border-b-0 xl:border-r xl:[&:nth-child(even)]:border-r-0 xl:[&:nth-last-child(-n+2)]:border-b-0"
            >
              <p className="min-w-0 break-words text-xs font-semibold leading-5 text-slate-100">{branch.name}</p>
              <div className="grid gap-0.5">
                {branch.phones.map((phone) => (
                  <a
                    key={phone.href}
                    className="block w-fit whitespace-nowrap text-xs leading-5 text-indigo-200 transition-colors hover:text-indigo-300"
                    href={phone.href}
                  >
                    {phone.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
          <div className="grid min-w-0 gap-1 border-b border-white/10 px-3 py-2.5 last:border-b-0 sm:border-r sm:[&:nth-child(even)]:border-r-0 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:border-r-0 lg:[&:nth-last-child(-n+2)]:border-b lg:last:border-b-0 xl:border-r xl:[&:nth-child(even)]:border-r-0 xl:[&:nth-last-child(-n+2)]:border-b-0">
            <p className="min-w-0 break-words text-xs font-semibold leading-5 text-slate-100">Email</p>
            <a
              className="block w-fit break-words text-xs leading-5 text-indigo-200 transition-colors hover:text-indigo-300"
              href="mailto:info@nexlanka.com"
            >
              info@nexlanka.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterLinks;
